import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Pagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Search as SearchIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
  PlayArrow as ProcessIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  PictureAsPdf as PdfIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Schedule as PendingIcon,
  ChevronRight as ChevronRightIcon,
  Receipt as InvoicesIcon
} from '@mui/icons-material';
import { invoiceService } from '../../services/invoice.service';
import { INVOICE_API_BASE_URL, INVOICE_API_KEY } from '../../config/api.config';

const DocumentList = ({ basket, onBack, onViewDocument, onProcessDocument, onPdfReview, onUploadView, refreshTrigger, onBasketChange, onBackToDashboard }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfDocumentId, setPdfDocumentId] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [availableBaskets, setAvailableBaskets] = useState([]);

  useEffect(() => {
    fetchDocuments();
    fetchBaskets();
  }, [basket, refreshTrigger]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await invoiceService.getDocuments(basket);
      const documents = response.documents || [];
      
      // Fetch metadata for each document to get complete status information
      const documentsWithMetadata = await Promise.all(
        documents.map(async (doc) => {
          try {
            const metadataResponse = await invoiceService.getDocumentMetadata(doc.document_id);
            return {
              ...doc,
              metadata: metadataResponse.metadata,
              processing_metadata: metadataResponse.metadata // Ensure processing_metadata is available
            };
          } catch (err) {
            console.warn(`Failed to fetch metadata for document ${doc.document_id}:`, err);
            return doc; // Return original document if metadata fetch fails
          }
        })
      );
      
      setDocuments(documentsWithMetadata);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchBaskets = async () => {
    try {
      const response = await invoiceService.getBaskets();
      setAvailableBaskets(response.baskets || []);
    } catch (err) {
      console.error('Failed to fetch baskets:', err);
    }
  };

  const handleBasketChange = (event) => {
    const newBasket = event.target.value;
    if (onBasketChange) {
      onBasketChange(newBasket);
    }
  };

  const handleMenuOpen = (event, document) => {
    setAnchorEl(event.currentTarget);
    setSelectedDocument(document);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDocument(null);
  };


  // Simplified - no status display needed

  // Simplified - no currency formatting needed

  const handlePdfView = async (documentId) => {
    setPdfDocumentId(documentId);
    setPdfViewerOpen(true);
    setPdfLoading(true);
    setPdfError(null);
    setPdfBlobUrl(null);

    try {
      const pdfUrl = `${INVOICE_API_BASE_URL}/api/v1/documents/${documentId}/download`;
      
      // Fetch the PDF with proper authentication
      const response = await fetch(pdfUrl, {
        headers: {
          'X-API-Key': INVOICE_API_KEY
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfBlobUrl(blobUrl);
      setPdfLoading(false);
    } catch (error) {
      console.error('Error fetching PDF:', error);
      setPdfError('Failed to load PDF document. Please try again.');
      setPdfLoading(false);
    }
  };

  const closePdfViewer = () => {
    setPdfViewerOpen(false);
    setPdfDocumentId(null);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setPdfError(null);
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  // Get Process Status (based on processing_status)
  const getProcessStatusInfo = (doc) => {
    const processingStatus = doc.metadata?.processing_status || doc.processing_status;
    
    if (processingStatus === 'completed' || processingStatus === 'success') {
      return {
        status: 'completed',
        label: 'Completed',
        color: 'success',
        icon: <SuccessIcon />
      };
    } else if (processingStatus === 'failed' || processingStatus === 'error') {
      return {
        status: 'failed',
        label: 'Failed',
        color: 'error',
        icon: <ErrorIcon />
      };
    } else if (processingStatus === 'processing' || processingStatus === 'in_progress') {
      return {
        status: 'processing',
        label: 'Processing',
        color: 'info',
        icon: <ProcessIcon />
      };
    } else {
      return {
        status: 'pending',
        label: 'Pending',
        color: 'warning',
        icon: <PendingIcon />
      };
    }
  };

  // Get Validation Status (based on po_validation_status)
  const getValidationStatusInfo = (doc) => {
    const poValidationStatus = doc.metadata?.po_validation_status;
    
    if (poValidationStatus === 'valid') {
      return {
        status: 'valid',
        label: 'Valid',
        color: 'success',
        icon: <SuccessIcon />
      };
    } else if (poValidationStatus === 'invalid') {
      return {
        status: 'invalid',
        label: 'Invalid',
        color: 'error',
        icon: <ErrorIcon />
      };
    } else if (poValidationStatus === 'pending') {
      return {
        status: 'pending',
        label: 'Pending',
        color: 'warning',
        icon: <PendingIcon />
      };
    } else {
      return {
        status: 'unknown',
        label: 'N/A',
        color: 'default',
        icon: <PendingIcon />
      };
    }
  };

  // Get Routed Basket (based on routed_to_basket)
  const getRoutedBasketInfo = (doc) => {
    const routedBasket = doc.metadata?.routed_to_basket || doc.metadata?.final_basket || doc.basket_name;
    
    // Debug logging to see what's available
    console.log('Document routed basket debug:', {
      document_id: doc.document_id,
      metadata: doc.metadata,
      routed_to_basket: doc.metadata?.routed_to_basket,
      final_basket: doc.metadata?.final_basket,
      basket_name: doc.basket_name,
      final_routed_basket: routedBasket
    });
    
    if (routedBasket === 'ready_to_pay') {
      return {
        status: 'ready',
        label: 'Ready to Pay',
        color: 'success',
        icon: <SuccessIcon />
      };
    } else if (routedBasket === 'further_validation') {
      return {
        status: 'validation',
        label: 'Needs Review',
        color: 'warning',
        icon: <WarningIcon />
      };
    } else if (routedBasket === 'processed_documents') {
      return {
        status: 'processed',
        label: 'Processed',
        color: 'info',
        icon: <ProcessIcon />
      };
    } else if (routedBasket === 'raw_invoices') {
      return {
        status: 'raw',
        label: 'Raw Documents',
        color: 'warning',
        icon: <PendingIcon />
      };
    } else if (routedBasket) {
      return {
        status: 'routed',
        label: routedBasket.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
        color: 'info',
        icon: <ProcessIcon />
      };
    } else {
      return {
        status: 'unknown',
        label: 'N/A',
        color: 'default',
        icon: <PendingIcon />
      };
    }
  };

  const filteredDocuments = documents.filter(doc => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      doc.name?.toLowerCase().includes(searchLower) ||
      doc.document_id?.toLowerCase().includes(searchLower) ||
      doc.processing_metadata?.invoice_number?.toLowerCase().includes(searchLower) ||
      doc.processing_metadata?.customer_id?.toLowerCase().includes(searchLower)
    );
  });

  const paginatedDocuments = filteredDocuments.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const getBasketTitle = (basketName) => {
    switch (basketName) {
      case 'raw_invoices':
        return 'Raw Documents';
      case 'processed_documents':
        return 'Processed Documents';
      case 'ready_to_pay':
        return 'Ready to Pay';
      case 'further_validation':
        return 'Needs Review';
      default:
        return basketName?.replace('_', ' ').toUpperCase() || 'All Documents';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={fetchDocuments}>
          Retry
        </Button>
      }>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Box display="flex" alignItems="center" mb={2}>
        <Button
          startIcon={<InvoicesIcon />}
          onClick={onBackToDashboard || onBack}
          sx={{ textTransform: 'none' }}
        >
          Invoices
        </Button>
        <ChevronRightIcon sx={{ mx: 1, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">
          {getBasketTitle(basket)}
        </Typography>
      </Box>

      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Box flexGrow={1}>
          <Typography variant="h4" component="h1">
            {getBasketTitle(basket)}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {filteredDocuments.length} document(s) found
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<CloudUploadIcon />}
          onClick={onUploadView}
          sx={{ 
            textTransform: 'none',
            fontWeight: 'medium',
            px: 3
          }}
        >
          Upload Document
        </Button>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Basket</InputLabel>
              <Select
                value={basket || ''}
                label="Basket"
                onChange={handleBasketChange}
              >
                <MenuItem value="">
                  <em>All Baskets</em>
                </MenuItem>
                {availableBaskets.map((basketItem) => (
                  <MenuItem key={basketItem.name} value={basketItem.name}>
                    {getBasketTitle(basketItem.name)} ({basketItem.document_count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              disabled
            >
              Filters
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Original Filename</TableCell>
                <TableCell>Original Timestamp</TableCell>
                <TableCell>Upload Time</TableCell>
                <TableCell>Process Status</TableCell>
                <TableCell>Validation Status</TableCell>
                <TableCell>Routed Basket</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedDocuments.map((doc) => (
                <TableRow key={doc.document_id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {doc.processing_metadata?.original_filename || doc.name || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(doc.processing_metadata?.original_timestamp) || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(doc.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const processStatusInfo = getProcessStatusInfo(doc);
                      return (
                        <Chip
                          label={processStatusInfo.label}
                          color={processStatusInfo.color}
                          size="small"
                          icon={processStatusInfo.icon}
                          variant="outlined"
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const validationStatusInfo = getValidationStatusInfo(doc);
                      return (
                        <Chip
                          label={validationStatusInfo.label}
                          color={validationStatusInfo.color}
                          size="small"
                          icon={validationStatusInfo.icon}
                          variant="outlined"
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const routedBasketInfo = getRoutedBasketInfo(doc);
                      return (
                        <Chip
                          label={routedBasketInfo.label}
                          color={routedBasketInfo.color}
                          size="small"
                          icon={routedBasketInfo.icon}
                          variant="outlined"
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => onViewDocument(doc.document_id)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="PDF Review">
                      <IconButton
                        size="small"
                        onClick={() => handlePdfView(doc.document_id)}
                        sx={{ 
                          ml: 0.5,
                          backgroundColor: 'primary.light',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'primary.main'
                          }
                        }}
                      >
                        <PdfIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    {doc.basket_name === 'raw_invoices' && (
                      <Tooltip title="Process Document">
                        <IconButton
                          size="small"
                          onClick={() => onProcessDocument(doc.document_id)}
                          sx={{ 
                            ml: 0.5,
                            backgroundColor: 'success.light',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'success.main'
                            }
                          }}
                        >
                          <ProcessIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, doc)}
                      sx={{ ml: 0.5 }}
                    >
                      <MoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Inline PDF Viewer */}
      {pdfViewerOpen && (
        <Card sx={{ mt: 3 }}>
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'primary.light',
              color: 'white'
            }}
          >
            <Typography variant="h6">
              PDF Document Viewer
            </Typography>
            <IconButton
              onClick={closePdfViewer}
              sx={{ color: 'white' }}
            >
              <BackIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ p: 2 }}>
            {pdfLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  Loading PDF...
                </Typography>
              </Box>
            )}
            
            {pdfError && (
              <Box textAlign="center" py={4}>
                <Typography variant="h6" color="error" gutterBottom>
                  Error Loading PDF
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {pdfError}
                </Typography>
              </Box>
            )}
            
            {!pdfLoading && !pdfError && pdfBlobUrl && (
              <Box
                sx={{
                  width: '100%',
                  height: '600px',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  overflow: 'hidden'
                }}
              >
                <iframe
                  src={pdfBlobUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title="PDF Document"
                />
              </Box>
            )}
          </Box>
        </Card>
      )}

      {/* Pagination */}
      {filteredDocuments.length > itemsPerPage && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(filteredDocuments.length / itemsPerPage)}
            page={page}
            onChange={(e, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem disabled>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DocumentList;
